import type { DirectoryPublic } from '@repo/types/directory';
import type { FilePublicWithPermissions } from '@repo/types/files';
import type { ColumnDef, SortingState } from '@tanstack/react-table';

import { getCoreRowModel, getFilteredRowModel, getSortedRowModel, useReactTable } from '@tanstack/react-table';
import { format, isValid, parseISO } from 'date-fns';
import { useMemo } from 'react';

import { useLocalStorage } from '@/hooks/local-storage';

// Custom hook for table configuration
export function useFilesTable(data: (DirectoryPublic | FilePublicWithPermissions)[]) {
  const [sorting, setSorting] = useLocalStorage<SortingState>('file-system-sort', [{ id: 'Nazwa', desc: false }]);

  // Define columns
  const columns: ColumnDef<DirectoryPublic | FilePublicWithPermissions>[] = useMemo(
    () => [
      {
        meta: 'Nazwa',
        id: 'Nazwa',
        accessorKey: 'name',
        header: 'Nazwa',
        enableSorting: true,
        enableFilters: true,
      },
      {
        meta: 'Data utworzenia',
        id: 'Data utworzenia',
        accessorKey: 'createdAt',
        header: 'Data utworzenia',
        sortType: 'datetime',
        sortingFn: 'datetime',
        cell: ({ getValue }) => {
          const date = getValue();
          if (typeof date === 'string' && isValid(parseISO(date))) {
            return format(new Date(date), 'dd.MM.yyyy');
          }
          return date;
        },
      },
      {
        meta: 'Data modyfikacji',
        id: 'Data modyfikacji',
        accessorKey: 'updatedAt',
        header: 'Data modyfikacji',
        sortType: 'datetime',
        sortingFn: 'datetime',
        cell: ({ getValue }) => {
          const date = getValue();
          if (typeof date === 'string' && isValid(parseISO(date))) {
            return format(new Date(date), 'dd.MM.yyyy');
          }
          return date;
        },
      },
    ],
    []
  );

  // Create table instance
  const dataModel = useReactTable({
    data,
    columns,
    getCoreRowModel: getCoreRowModel(),
    enableRowSelection: true,
    enableFilters: true,
    getFilteredRowModel: getFilteredRowModel(),
    onSortingChange: setSorting,
    getSortedRowModel: getSortedRowModel(),
    state: {
      sorting,
    },
  });
  return { dataModel, sorting };
}
